import { App, AppContext } from "Core";

/**
 * ## BaseService.
 *
 * Base class for all services. Contains methods for ajax requests.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Execute post request with error handling:
 * DMS.Service.Base.post('url', data).then(function(result) {
 *     // result is set here.
 * });
 * ```
 */
export class BaseService {
    protected getBeforeSend(): any {
        return function (xhr: JQueryXHR) {
            let app = <App>(<any>window).app;
            if (app) {
                if (app.isDmsConfig) {
                    xhr.setRequestHeader("dmsConfig", "dmsConfig");
                }
                if (app.auth && app.auth.user && app.auth.user.access_token) {
                    xhr.setRequestHeader("Authorization", "Bearer " + app.auth.user.access_token);
                }
            }
        };
    }

    /**
     * Performs an ajax request with the given options.
     * @param options Options for the request.
     */
    private ajaxRequest<T>(options: JQueryAjaxSettings): Promise<T> {
        return new Promise((resolve, reject) => {
            $.ajax(
                $.extend(
                    {
                        contentType: "application/json",
                        dataType: "json",
                    },
                    options
                )
            )
                .done(resolve)
                .fail(reject);
        });
    }

    /**
     * Performs a get-operation on the server.
     * @param url Url of the service.
     * @param T Type of result.
     * @returns Promise which resolves when the request is finished.
     * @since 4.0.0
     */
    public ajaxget<T>(url: string): Promise<T> {
        return this.ajaxRequest<T>({
            url: url,
            type: "GET",
            error: function (xhr, textStatus, error) {
                console.error(error + " - " + textStatus);
            },
        });
    }

    /**
     * Performs a post-operation on the server.
     * @param url Url of the service.
     * @param data Data, which will be converted to JSON and sent as body.
     * @param dataType for the request. Defaults to "json".
     * @param T Type of result.
     * @returns Promise which resolves when the request is finished.
     * @since 4.0.0
     */
    public post<T>(url: string, data: Object, dataType?: string): Promise<T> {
        return this.handleResult(
            this.ajaxRequest<T>({
                url: url,
                type: "POST",
                data: JSON.stringify(data),
                dataType: dataType || "json",
            })
        );
    }

    /**
     * Handle error messages and timeouts before providing the result to the caller.
     * @param promise Result promise.
     * @hidden
     */
    public handleResult<T>(promise: Promise<T>): Promise<T> {
        return promise.then(
            (res) => {
                return AppContext.handleServiceResult(res);
            },
            (res) => {
                return AppContext.handleServiceError(res);
            }
        );
    }

    /**
     * Performs a delete-operation on the server.
     * @param url Url of the service.
     * @param T Type of result.
     * @returns Promise which resolves when the request is finished.
     * @since 4.0.0
     */
    public delete<T>(url: string): Promise<T> {
        return this.ajaxRequest<T>({
            url: url,
            type: "DELETE",
            error: function (xhr, textStatus, error) {
                console.error(error + " - " + textStatus);
            },
        });
    }
}
